import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../components/button.tsx";
import { ErrorBanner } from "../../components/error-banner.tsx";
import { InfoCard } from "../../components/info-card.tsx";
import { Input } from "../../components/input.tsx";

interface LoginContext {
  appName: string;
}

type ContextState =
  | { status: "loading" }
  | { status: "ok"; data: LoginContext }
  | { status: "error"; message: string };

const PASS_THROUGH_PARAMS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "state",
  "scope",
  "nonce",
] as const;

export function OidcSignIn() {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams] = useSearchParams();
  const [context, setContext] = useState<ContextState>({ status: "loading" });

  useEffect(() => {
    if (!appId) {
      return;
    }
    const params = new URLSearchParams();
    for (const key of PASS_THROUGH_PARAMS) {
      const value = searchParams.get(key);
      if (value !== null) {
        params.set(key, value);
      }
    }

    fetch(`/oidc/${appId}/login-context?${params.toString()}`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}) as { error?: string });
        if (!response.ok) {
          const error = (body as { error?: string }).error ?? `HTTP ${response.status}`;
          setContext({ status: "error", message: error });
          return;
        }
        setContext({ status: "ok", data: body as LoginContext });
      })
      .catch((error: Error) => {
        setContext({ status: "error", message: error.message });
      });
  }, [appId, searchParams]);

  if (!appId) {
    return null;
  }

  if (context.status === "loading") {
    return <InfoCard title="Loading…" />;
  }

  if (context.status === "error") {
    return (
      <InfoCard
        title="Cannot sign in"
        description={describeOidcError(context.message)}
      />
    );
  }

  const errorMessage = searchParams.get("error");

  return (
    <div className="mx-auto mt-24 max-w-md px-6">
      <div className="border-2 border-ink p-8">
        <h1 className="font-mono text-xl font-bold uppercase tracking-[0.1em]">
          {context.data.appName}
        </h1>
        <p className="mt-2 font-mono text-xs uppercase tracking-wider text-muted">
          Sign in to continue
        </p>

        <ErrorBanner message={errorMessage} className="mt-6" />

        <form
          method="post"
          action={`/oidc/${appId}/authorize`}
          className="mt-6 space-y-4"
        >
          {PASS_THROUGH_PARAMS.map((name) => (
            <input
              key={name}
              type="hidden"
              name={name}
              value={searchParams.get(name) ?? ""}
            />
          ))}
          <Input
            type="password"
            name="access_code"
            autoComplete="current-password"
            placeholder="Access code"
            required
            autoFocus
            className="w-full"
          />
          <Button type="submit" className="w-full">
            Sign in
          </Button>
        </form>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-wider text-muted">
        <span>
          Powered by{" "}
          <a href="https://authed.co" className="no-underline">
            authed.co
          </a>
        </span>
        <Link to="/terms" className="no-underline">
          Terms
        </Link>
        <Link to="/privacy" className="no-underline">
          Privacy
        </Link>
      </div>
    </div>
  );
}

function describeOidcError(error: string): string {
  switch (error) {
    case "unknown_application":
      return "This application doesn't exist.";
    case "invalid_client_id":
      return "The client_id in the URL doesn't match this application.";
    case "unsupported_response_type":
      return "Only response_type=code is supported.";
    case "redirect_uri_not_registered":
      return "The redirect_uri in the URL is not registered for this application.";
    default:
      return error;
  }
}
